import React, { useEffect } from 'react';
import {
    IdsButton,
    IdsDivider
} from '@investec/ids-web-react';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';

import './isaTransferSuccess.scss';

type TransferType = 'investec' | 'provider';

interface IsaTransferSuccessStepProps {
    onClose: () => void;
    transferType?: TransferType;
    providerName?: string;
}

const IsaTransferSuccessStep: React.FC<IsaTransferSuccessStepProps> = ({
    onClose,
    transferType = 'investec',
    providerName
}) => {
    const analytics = useUnifiedAnalytics();
    const isProviderFlow = transferType === 'provider';

    useEffect(() => {
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                `ISA transfer success page loaded - ${transferType} flow`,
                {},
                'ISA Transfer',
                `ISA_Transfer_Success|Page_Load|Success_View|${transferType}`
            );
        }
    }, [analytics, transferType]);

    const handleReturnToDashboard = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'return to dashboard',
                '',
                'isa transfer success',
                'ISA Transfer',
                '',
                {},
                'click',
                `ISA_Transfer_Success|Button_Click|Return_To_Dashboard|${transferType}`,
                'ISA Transfer Success'
            );
        }
        onClose();
    };

    return (
        <main className="isa-transfer-success">
            <div className="isa-transfer-success__content">
                <div className="isa-transfer-success__heading">
                    <svg className="isa-transfer-success__icon" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M24 6C14.0589 6 6 14.0589 6 24C6 33.9411 14.0589 42 24 42C33.9411 42 42 33.9411 42 24C42 14.0589 33.9411 6 24 6ZM2 24C2 11.8497 11.8497 2 24 2C36.1503 2 46 11.8497 46 24C46 36.1503 36.1503 46 24 46C11.8497 46 2 36.1503 2 24ZM34.4142 16.5858C35.1953 17.3668 35.1953 18.6332 34.4142 19.4142L22.4142 31.4142C21.6332 32.1953 20.3668 32.1953 19.5858 31.4142L13.5858 25.4142C12.8047 24.6332 12.8047 23.3668 13.5858 22.5858C14.3668 21.8047 15.6332 21.8047 16.4142 22.5858L21 27.1716L31.5858 16.5858C32.3668 15.8047 33.6332 15.8047 34.4142 16.5858Z" fill="#286B6E" />
                    </svg>
                    {isProviderFlow ? (
                        <h1>Your transfer is now in progress.</h1>
                    ) : (
                        <h1>Your transfer is complete.</h1>
                    )}
                </div>

                {isProviderFlow ? (
                    <p className="isa-transfer-success__intro">
                        We&rsquo;ll send you a confirmation email shortly.
                    </p>
                ) : (
                    <p className="isa-transfer-success__intro">
                        We&rsquo;ll send you a confirmation message shortly.
                    </p>
                )}

                <IdsDivider />

                {isProviderFlow ? (
                    <div className="isa-transfer-success__next-steps">
                        <h2>What happens now?</h2>

                        <div className="isa-transfer-success__timeline">
                            {/* Step 1: We arrange your transfer */}
                            <div className="isa-transfer-success__timeline-step">
                                <div className="isa-transfer-success__timeline-step-header">
                                    <div className="isa-transfer-success__timeline-dot" />
                                    <h3>We arrange your transfer</h3>
                                </div>
                                <div className="isa-transfer-success__timeline-row">
                                    <div className="isa-transfer-success__timeline-line-wrap">
                                        <div className="isa-transfer-success__timeline-line" />
                                    </div>
                                    <div className="isa-transfer-success__timeline-step-body">
                                        {providerName ? (
                                            <p>We&rsquo;ll contact {providerName} to request the secure transfer of your savings to Investec Save.</p>
                                        ) : (
                                            <p>We&rsquo;ll contact your current provider to request the secure transfer of your savings to Investec Save.</p>
                                        )}
                                        <p>Transfers can take up to 15 business days.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Step 2: We'll keep you updated */}
                            <div className="isa-transfer-success__timeline-step">
                                <div className="isa-transfer-success__timeline-step-header">
                                    <div className="isa-transfer-success__timeline-dot" />
                                    <h3>We&rsquo;ll keep you updated</h3>
                                </div>
                                <div className="isa-transfer-success__timeline-row">
                                    <div className="isa-transfer-success__timeline-line-wrap">
                                        <div className="isa-transfer-success__timeline-line" />
                                    </div>
                                    <div className="isa-transfer-success__timeline-step-body">
                                        <p>We&rsquo;ll send email updates during the transfer process, and we&rsquo;ll let you know as soon as your savings are transferred into your ISA account.</p>
                                        <p>If you have any remaining annual ISA allowance this tax year, you can continue adding money to your Instant Access Cash ISA.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="isa-transfer-success__next-steps">
                        <h2>What happens now?</h2>
                        <p>We&rsquo;ll instantly transfer your savings to your selected Investec Save cash ISA. Your savings will retain their tax-free status.</p>
                    </div>
                )}

                <IdsButton appearance="primary" onClick={handleReturnToDashboard}>
                    Return to your dashboard
                </IdsButton>
            </div>
        </main>
    );
};

export default IsaTransferSuccessStep;
